import React, { useEffect, useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';
import { supabase } from '../../lib/supabase';
import SafeIcon from '../../common/SafeIcon';
import * as FiIcons from 'react-icons/fi';
import { format } from 'date-fns';

const ClientDetail = () => {
  const { clientId } = useParams();
  const { profile } = useAuth();
  const navigate = useNavigate();
  
  const [client, setClient] = useState(null);
  const [matters, setMatters] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    const fetchClientData = async () => {
      if (!profile?.advisor_firm_id || !clientId) return;

      try {
        setLoading(true);
        setError(null);

        // 1. Fetch Client Profile
        const { data: clientData, error: clientErr } = await supabase
          .from('clients')
          .select('*')
          .eq('id', clientId)
          .eq('advisor_firm_id', profile.advisor_firm_id)
          .single();

        if (clientErr) {
          console.error('ClientDetail: Fetch client error', clientErr);
          throw new Error("Client not found or access denied.");
        }
        setClient(clientData);

        // 2. Fetch Client Matters
        const { data: mattersData, error: mattersErr } = await supabase
          .from('matters')
          .select('*')
          .eq('client_id', clientId)
          .order('created_at', { ascending: false });

        if (mattersErr) {
          console.error('ClientDetail: Fetch matters error', mattersErr);
          throw mattersErr;
        }
        setMatters(mattersData || []);

      } catch (err) {
        setError(err.message);
      } finally {
        setLoading(false);
      }
    };

    fetchClientData();
  }, [clientId, profile]);

  const getStatusBadge = (status) => {
    const label = status === 'matter_created'
      ? 'plan created'
      : status?.replace('matter', 'plan').replaceAll('_', ' ');
    const colors = status === 'completed' 
      ? 'bg-green-50 text-green-700 border-green-100' 
      : 'bg-blue-50 text-blue-700 border-blue-100';
    
    return (
      <span className={`px-2 py-0.5 text-[10px] font-bold uppercase tracking-wider rounded-full border ${colors}`}>
        {label}
      </span>
    );
  };

  const getPortalStatus = () => {
    if (client.user_id) {
      return {
        label: 'Portal Active',
        dot: 'bg-green-500'
      };
    }

    if (client.invitation_status === 'invited') {
      return {
        label: 'Invited',
        dot: 'bg-blue-500'
      };
    }

    return {
      label: 'Not Invited',
      dot: 'bg-slate-300'
    };
  };

  if (loading) {
    return (
      <div className="flex flex-col items-center justify-center py-20">
        <SafeIcon icon={FiIcons.FiLoader} className="animate-spin h-10 w-10 text-slate-300 mb-4" />
        <p className="text-slate-500">Loading client profile...</p>
      </div>
    );
  }

  if (error || !client) {
    return (
      <div className="max-w-2xl mx-auto mt-12 p-8 bg-white rounded-xl shadow-sm border border-red-100 text-center">
        <SafeIcon icon={FiIcons.FiAlertCircle} className="h-12 w-12 text-red-400 mx-auto mb-4" />
        <h2 className="text-xl font-bold text-slate-900">Unable to load client</h2>
        <p className="text-slate-600 mt-2">{error || "Client record unavailable."}</p>
        <button 
          onClick={() => navigate('/clients')}
          className="mt-6 inline-flex items-center px-4 py-2 text-sm font-medium text-slate-700 bg-white border border-slate-300 rounded-md hover:bg-slate-50"
        >
          Back to client list
        </button>
      </div>
    );
  }

  const portalStatus = getPortalStatus();

  return (
    <div className="max-w-5xl mx-auto space-y-8 pb-12">
      {/* Header & Navigation */}
      <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
        <div className="flex items-center">
          <button 
            onClick={() => navigate('/clients')}
            className="mr-4 p-2 rounded-full hover:bg-white transition-colors text-slate-400 hover:text-slate-600"
          >
            <SafeIcon icon={FiIcons.FiArrowLeft} className="h-6 w-6" />
          </button>
          <div>
            <h1 className="text-2xl font-bold text-slate-900 leading-none">
              {client.first_name} {client.last_name}
            </h1>
            <p className="text-sm text-slate-500 mt-2 flex items-center">
              <span className={`w-2 h-2 rounded-full mr-2 ${portalStatus.dot}`}></span>
              Status: <span className="ml-1 font-medium">{portalStatus.label}</span>
            </p>
          </div>
        </div>
        
        <div className="flex items-center space-x-3">
          <button 
            onClick={() => navigate(`/matters/create?clientId=${client.id}`)}
            className="inline-flex items-center px-4 py-2 text-sm font-medium text-white bg-slate-800 rounded-md hover:bg-slate-900 transition-colors shadow-sm"
          >
            <SafeIcon icon={FiIcons.FiPlus} className="mr-2 h-4 w-4" />
            Create Plan
          </button>
        </div>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
        {/* Left Column: Client Info Card */}
        <div className="space-y-6">
          <div className="bg-white shadow-sm rounded-xl border border-slate-200 overflow-hidden">
            <div className="px-6 py-4 border-b border-slate-100 bg-slate-50/50">
              <h3 className="text-xs font-bold text-slate-400 uppercase tracking-widest">Contact Information</h3>
            </div>
            <div className="p-6 space-y-5">
              <div>
                <label className="block text-[10px] font-bold text-slate-400 uppercase tracking-widest mb-1">Email Address</label>
                <div className="flex items-center text-slate-900 group">
                  <SafeIcon icon={FiIcons.FiMail} className="h-4 w-4 mr-2 text-slate-300" />
                  <span className="text-sm font-medium">{client.email}</span>
                </div>
              </div>
              <div>
                <label className="block text-[10px] font-bold text-slate-400 uppercase tracking-widest mb-1">Phone Number</label>
                <div className="flex items-center text-slate-900">
                  <SafeIcon icon={FiIcons.FiPhone} className="h-4 w-4 mr-2 text-slate-300" />
                  <span className="text-sm font-medium">{client.phone || 'Not provided'}</span>
                </div>
              </div>
              <div>
                <label className="block text-[10px] font-bold text-slate-400 uppercase tracking-widest mb-1">Residence</label>
                <div className="flex items-center text-slate-900">
                  <SafeIcon icon={FiIcons.FiMapPin} className="h-4 w-4 mr-2 text-slate-300" />
                  <span className="text-sm font-medium">{client.state_of_residence}</span>
                </div>
              </div>
              <div className="pt-4 border-t border-slate-50">
                <label className="block text-[10px] font-bold text-slate-400 uppercase tracking-widest mb-1">Added On</label>
                <p className="text-sm text-slate-600">{format(new Date(client.created_at), 'MMMM d, yyyy')}</p>
              </div>
            </div>
          </div>

          <div className="bg-slate-50 rounded-xl border border-slate-200 p-6 text-center">
            <p className="text-xs font-bold text-slate-400 uppercase tracking-widest mb-1">Total Plans</p>
            <p className="text-3xl font-bold text-slate-900">{matters.length}</p>
          </div>
        </div>

        {/* Right Column: Matters List */}
        <div className="lg:col-span-2 space-y-6">
          <div className="bg-white shadow-sm rounded-xl border border-slate-200 overflow-hidden">
            <div className="px-6 py-4 border-b border-slate-100 bg-slate-50/50 flex justify-between items-center">
              <h3 className="text-xs font-bold text-slate-800 uppercase tracking-widest">Client Plans</h3>
            </div>
            
            {matters.length > 0 ? (
              <div className="divide-y divide-slate-100">
                {matters.map((matter) => (
                  <div 
                    key={matter.id} 
                    className="p-6 hover:bg-slate-50 transition-colors cursor-pointer flex items-center justify-between"
                    onClick={() => navigate(`/matters/${matter.id}`)}
                  >
                    <div className="space-y-1">
                      <div className="flex items-center gap-3">
                        <h4 className="font-semibold text-slate-900">{matter.plan_type}</h4>
                        {getStatusBadge(matter.status)}
                      </div>
                      <div className="flex items-center text-xs text-slate-500 gap-4">
                        <span className="flex items-center">
                          <SafeIcon icon={FiIcons.FiCalendar} className="mr-1 h-3 w-3" />
                          {format(new Date(matter.created_at), 'MMM d, yyyy')}
                        </span>
                        <span className="flex items-center">
                          <SafeIcon icon={FiIcons.FiBarChart2} className="mr-1 h-3 w-3" />
                          Complexity: <span className="ml-1 capitalize font-medium text-slate-700">{matter.complexity_level}</span>
                        </span>
                      </div>
                    </div>
                    <div className="flex items-center gap-4">
                      <div className="text-right hidden sm:block">
                        <p className="text-[10px] font-bold text-slate-400 uppercase tracking-widest">Legal Fee</p>
                        <p className="text-xs font-medium text-slate-700 capitalize">{matter.legal_fee_status?.replace('_', ' ')}</p>
                      </div>
                      <SafeIcon icon={FiIcons.FiChevronRight} className="h-5 w-5 text-slate-300" />
                    </div>
                  </div>
                ))}
              </div>
            ) : (
              <div className="p-12 text-center">
                <SafeIcon icon={FiIcons.FiInbox} className="mx-auto h-12 w-12 text-slate-200 mb-4" />
                <p className="text-slate-500 text-sm">No plans have been created for this client yet.</p>
                <button 
                  onClick={() => navigate(`/matters/create?clientId=${client.id}`)}
                  className="mt-4 text-sm font-bold text-blue-600 hover:text-blue-800 uppercase tracking-widest"
                >
                  Start a new plan
                </button>
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default ClientDetail;
